/**
 * Shared Cabinet5 front style: hinged door (default) or 1–5 stacked drawers,
 * plus drawer handle length (short / long / none).
 * Stored on `symbol.threeD.cabinetDrawerCount` (0 = door) and
 * `symbol.threeD.cabinetHandleStyle`.
 */

export const CABINET_DRAWER_COUNTS = [0, 1, 2, 3, 4, 5] as const;
export type CabinetDrawerCount = (typeof CABINET_DRAWER_COUNTS)[number];

export const CABINET_HANDLE_STYLES = ["long", "short", "none"] as const;
export type CabinetHandleStyle = (typeof CABINET_HANDLE_STYLES)[number];

export const DEFAULT_CABINET_DRAWER_COUNT: CabinetDrawerCount = 0;
export const DEFAULT_CABINET_HANDLE_STYLE: CabinetHandleStyle = "long";

const CABINET_DRAWER_COUNT_SET = new Set<number>(CABINET_DRAWER_COUNTS);
const CABINET_HANDLE_STYLE_SET = new Set<string>(CABINET_HANDLE_STYLES);

/**
 * Resolves drawer count from `symbol.threeD`. Unknown / missing → door (0).
 */
export function resolveCabinetDrawerCount(
  cabinetDrawerCount: number | undefined | null,
): CabinetDrawerCount {
  if (
    typeof cabinetDrawerCount === "number" &&
    CABINET_DRAWER_COUNT_SET.has(cabinetDrawerCount)
  ) {
    return cabinetDrawerCount as CabinetDrawerCount;
  }
  return DEFAULT_CABINET_DRAWER_COUNT;
}

/**
 * Resolves drawer handle style from `symbol.threeD`. Unknown / missing → long.
 */
export function resolveCabinetHandleStyle(
  cabinetHandleStyle: string | undefined | null,
): CabinetHandleStyle {
  if (cabinetHandleStyle && CABINET_HANDLE_STYLE_SET.has(cabinetHandleStyle)) {
    return cabinetHandleStyle as CabinetHandleStyle;
  }
  return DEFAULT_CABINET_HANDLE_STYLE;
}

/** True when the front is stacked drawers instead of a single door. */
export function isCabinetDrawerFront(count: CabinetDrawerCount): boolean {
  return count >= 1;
}
